import useSearchResults from "@/src/hooks/srp/useSearchResults"
import { selectAILastSearchedInput, selectAILoading, type JourneyLeg } from "@/src/redux/slices/aiRecommendationSlice"
import { useAppSelector } from "@/src/redux/store"
import type { AIRecommendationSuggestionsResponse, RankingWeights } from "@/src/types/aiRecommendation"
import { smartSRPMessages } from "@/src/utils/srp/aiRecommendation/locale"
import { keyframes } from "@emotion/react"
import { Box, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { Sparkles } from "lucide-react"
import { useLocale } from "next-intl"
import { useEffect, useMemo, useState } from "react"

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`

const iconGlow = keyframes`
  0%, 100% { box-shadow: 0 0 12px rgba(33,150,243,0.3); }
  50% { box-shadow: 0 0 24px rgba(156,39,176,0.5); }
`

const sparkleSpin = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(0.85); }
  100% { transform: rotate(360deg) scale(1); }
`

const pulseExpand = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(1.4); opacity: 0; }
`

const barDance = keyframes`
  0%, 100% { height: 8px; opacity: 0.4; }
  50% { height: 28px; opacity: 1; }
`

const shimmerSlide = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`

const messageFadeSlide = keyframes`
  0% { opacity: 0.4; transform: translateY(4px); }
  20% { opacity: 1; transform: translateY(0); }
  80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0.4; transform: translateY(-4px); }
`

type ChipLabels = typeof smartSRPMessages['en']['preferenceChips']

const WEIGHT_CHIP_THRESHOLD = 0.12

type WeightKey = keyof RankingWeights
const WEIGHT_LABEL_MAP: Record<WeightKey, keyof ChipLabels> = {
    priceWeight: 'price',
    durationWeight: 'duration',
    layoverWeight: 'stops',
    departureWeight: 'departure',
    arrivalWeight: 'arrival',
}

function buildDynamicChips (
    data: AIRecommendationSuggestionsResponse | null,
    labels: ChipLabels
): string[] {
    if (!data?.weights) return [labels.price, labels.duration, labels.stops]

    const weights = data.weights
    const filters = data.suggestedFilters

    const chips: string[] = []

    const sorted = (Object.entries(weights) as [WeightKey, number][])
        .sort(([, a], [, b]) => b - a)

    for (const [key, value] of sorted) {
        if (value < WEIGHT_CHIP_THRESHOLD) continue
        const labelKey = WEIGHT_LABEL_MAP[key]
        if (!labelKey) continue

        if (key === 'layoverWeight' && (filters?.nonStop || filters?.stops?.includes(0))) {
            chips.push(labels.nonStop)
        } else {
            chips.push(labels[labelKey])
        }
    }

    if (filters?.checkedBaggageIncluded) {
        chips.push(labels.baggage)
    }

    return chips.length > 0 ? chips : [labels.price, labels.duration, labels.stops]
}

const CHIP_COLORS = [
    (t: any) => ({ color: t.palette.success.main, bg: alpha(t.palette.success.main, 0.08), border: alpha(t.palette.success.main, 0.35) }),
    (t: any) => ({ color: t.palette.primary.main, bg: alpha(t.palette.primary.main, 0.08), border: alpha(t.palette.primary.main, 0.35) }),
    (t: any) => ({ color: t.palette.secondary.main, bg: alpha(t.palette.secondary.main, 0.08), border: alpha(t.palette.secondary.main, 0.35) }),
    (t: any) => ({ color: t.palette.info.main, bg: alpha(t.palette.info.main, 0.08), border: alpha(t.palette.info.main, 0.35) }),
    (t: any) => ({ color: t.palette.warning.main, bg: alpha(t.palette.warning.main, 0.08), border: alpha(t.palette.warning.main, 0.35) }),
]

export const SmartSortMessage = () => {
    const locale = useLocale() as 'en' | 'ar'
    const smartSRPMessagesData = useMemo(() => smartSRPMessages[locale], [locale])
    const {
        selectedSortForSelectedJourneyLeg,
        selectedJourneyLeg,
        smartRecommendationData,
    } = useSearchResults()
    const leg = selectedJourneyLeg as JourneyLeg
    const theme = useTheme()
    const isAILoading = useAppSelector(state => selectAILoading(state, leg))
    const lastSearchedInput = useAppSelector(state => selectAILastSearchedInput(state, leg))
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
    const chips = useMemo(
        () => buildDynamicChips(smartRecommendationData, smartSRPMessagesData.preferenceChips),
        [smartRecommendationData, smartSRPMessagesData.preferenceChips]
    )
    const loadingMessages = useMemo(() => (
        locale === "ar"
            ? [
                "نفهم تفضيلات سفرك...",
                "نطبّق فلاترك ومتطلبات السفر...",
                "نرتّب الرحلات بناءً على تفضيلاتك...",
                "نحضّر توصياتك المخصصة...",
                "باقي القليل — نبحث عن أفضل الخيارات لك..."
            ]
            : [
                "Understanding your travel preferences...",
                "Applying your filters and travel requirements...",
                "Ranking flights based on your preferences...",
                "Preparing your personalized recommendations...",
                "Almost there — finding the best matches for you..."
            ]
    ), [locale])

    useEffect(() => {
        if (!isAILoading) {
            setLoadingMessageIndex(0)
            return
        }

        const intervalId = window.setInterval(() => {
            setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length)
        }, 1700)

        return () => window.clearInterval(intervalId)
    }, [isAILoading, loadingMessages.length])


    if (selectedSortForSelectedJourneyLeg?.sortKey !== 'smart') return null

    const hasUserPreferences = !!lastSearchedInput
    const banner = hasUserPreferences
        ? smartSRPMessagesData.smartSortBanner.withPreferences
        : smartSRPMessagesData.smartSortBanner.default


    if (isAILoading) {
        const primaryColor = theme.palette.primary.main
        const secondaryColor = theme.palette.secondary.main
        const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
        const loadingTitle = locale === "ar" ? "نعثر على أفضل الرحلات لك" : "Finding your best flights"

        return (
            <Box
                id="smart-sort-message"
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    maxHeight: 88,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(135deg, ${alpha(primaryColor, 0.04)}, ${alpha(secondaryColor, 0.08)}, ${alpha(primaryColor, 0.04)})`,
                        backgroundSize: "200% 200%",
                        animation: `${gradientShift} 3s ease infinite`,
                    }}
                />

                <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2, p: "16px 24px" }}>
                    <Box
                        sx={{
                            position: "relative",
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: 3,
                            background: gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            animation: `${iconGlow} 2s ease-in-out infinite`,
                        }}
                    >
                        <Box sx={{ animation: `${sparkleSpin} 3s linear infinite`, display: "inline-flex" }}>
                            <Sparkles size={20} color="currentColor" />
                        </Box>
                        {[0, 1].map(i => (
                            <Box
                                key={i}
                                sx={{
                                    position: "absolute",
                                    inset: -4,
                                    borderRadius: 4,
                                    border: `2px solid ${alpha(primaryColor, 0.4)}`,
                                    animation: `${pulseExpand} 2s ease-out infinite`,
                                    animationDelay: `${i}s`,
                                }}
                            />
                        ))}
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                background: gradient,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            {loadingTitle}
                        </Typography>
                        <Typography
                            key={loadingMessageIndex}
                            sx={{
                                fontSize: 13,
                                color: "text.secondary",
                                fontFamily: "monospace",
                                animation: `${messageFadeSlide} 1.7s ease-in-out`,
                            }}
                        >
                            {loadingMessages[loadingMessageIndex]}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 32, ml: "auto" }}>
                        {[0, 1, 2, 3, 4].map(i => (
                            <Box
                                key={i}
                                sx={{
                                    width: 4,
                                    borderRadius: 1,
                                    background: gradient,
                                    animation: `${barDance} 1s ease-in-out infinite`,
                                    animationDelay: `${i * 0.15}s`,
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
                        animation: `${shimmerSlide} 2.5s ease-in-out infinite`,
                    }}
                />
            </Box>
        )
    }

    return (
        <Box
            id="smart-sort-message"
            position="relative"
            overflow="hidden"
            border={`1px solid ${theme.palette.borders?.default ?? "#E0E0E0"}`}
            borderRadius={1}
            mb={1}
            p={2}
            sx={{
                background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 45%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)"
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    right: -32,
                    top: -32,
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    filter: "blur(24px)"
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: -24,
                    bottom: -24,
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                    filter: "blur(20px)"
                }}
            />

            <Box position="relative" zIndex={1} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent={{ xs: "flex-start", sm: "space-between" }}>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={36}
                        height={36}
                        borderRadius={1}
                        sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.10) }}
                    >
                        <Sparkles size={16} color={theme.palette.primary.main} />
                    </Box>
                    <Box>
                        <Typography fontSize={13} fontWeight={700} color={theme.palette.textColors?.primary ?? theme.palette.text.primary}>
                            {banner.heading}
                        </Typography>
                        <Typography fontSize={12} fontWeight={500} color={theme.palette.textColors?.secondary ?? theme.palette.text.secondary} sx={{ mt: 0.25 }}>
                            {banner.description}
                        </Typography>
                    </Box>
                </Box>

                {/* <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {chips.map((label, idx) => {
                        const { color, bg, border } = (CHIP_COLORS[idx % CHIP_COLORS.length])(theme)
                        return (
                            <Chip
                                key={label}
                                label={label}
                                variant="outlined"
                                sx={{
                                    backgroundColor: bg,
                                    borderColor: border,
                                    px: 0.75,
                                    height: 28,
                                    '& .MuiChip-label': {
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: theme.palette.textColors?.primary ?? theme.palette.text.primary,
                                        px: 0.75,
                                    },
                                }}
                            />
                        )
                    })}
                </Stack> */}
            </Box>

            {/* {banner.disclaimer && (
                <Box
                    position="relative"
                    zIndex={1}
                    display="flex"
                    alignItems="flex-start"
                    gap={0.75}
                    mt={1.5}
                    px={1.5}
                    py={1}
                    borderRadius={0.75}
                    sx={{ backgroundColor: alpha(theme.palette.info.main, 0.06) }}
                >
                    <Info size={14} color={theme.palette.info.main} style={{ flexShrink: 0, marginTop: 1 }} />
                    <Typography fontSize={11} fontWeight={400} color={theme.palette.textColors?.secondary ?? theme.palette.text.secondary} lineHeight={1.5}>
                        {banner.disclaimer}
                    </Typography>
                </Box>
            )} */}
        </Box>
    )
}
